import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskHeaderBlockConfig, LinkValue } from '@front-platform/content-builder';
import { kioskHeaderBlockResolveConfig } from '@front-platform/content-builder';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import {
  collectCatalogFilterOptions,
  hasCatalogFilterOptions,
  type CatalogFilterSelection,
} from '@shared/blocks/shop-product-list-block/catalog-filters';
import { ShopCatalogFiltersService } from '@shared/blocks/shop-product-list-block/shop-catalog-filters.service';

import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';

import { readKioskHeaderLogoUrl } from './kiosk-header-logo-url';

export interface KioskHeaderBlockRuntimeConfig {
  'color'?: string;
  'display_logo'?: boolean;
  'button_home_text'?: string;
  'button_background_color'?: IContentBuilderFieldColor;
  'button_text_color'?: IContentBuilderFieldColor;
  'blocks'?: unknown[];
}

export type KioskHeaderBlockConfigInput = Partial<
  Omit<KioskHeaderBlockConfig, keyof KioskHeaderBlockRuntimeConfig> & KioskHeaderBlockRuntimeConfig
>;
export type KioskHeaderBlockResolvedConfig = Omit<
  KioskHeaderBlockConfig,
  keyof KioskHeaderBlockRuntimeConfig
> &
  Partial<KioskHeaderBlockRuntimeConfig>;
export interface KioskHeaderBlockContract extends ConfigComponentContract<
  KioskHeaderBlockConfigInput,
  KioskHeaderBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-header-block',
  templateUrl: './kiosk-header-block.component.html',
  standalone: false,
})
export class KioskHeaderBlockComponent implements KioskHeaderBlockContract {
  readonly config = input<KioskHeaderBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskHeaderBlockConfig,
    KioskHeaderBlockRuntimeConfig
  >(kioskHeaderBlockResolveConfig, () => ({ config: this.config() }));
  readonly color = computed(() => this.resolvedConfig()['color'] as unknown as string);
  readonly displayLogo = computed(
    () => this.resolvedConfig()['display_logo'] as unknown as boolean,
  );
  readonly logo = computed(
    () =>
      this.resolvedConfig()['logo'] as unknown as {
        url: IContentBuilderFieldImage;
      },
  );
  readonly logoUrl = computed(
    () => this.resolvedConfig()['logo_url'] as unknown as string | LinkValue,
  );
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly backgroundImage = computed(
    () => this.resolvedConfig()['background_image'] as unknown as IContentBuilderFieldImage,
  );
  readonly backgroundUrl = computed(
    () => this.resolvedConfig()['background_url'] as unknown as string | LinkValue,
  );
  readonly headerLandingText = computed(
    () => this.resolvedConfig()['header_landing_text'] as unknown as string,
  );
  readonly displayHomeButton = computed(
    () => this.resolvedConfig()['display_home_button'] as unknown as boolean,
  );
  readonly displayBackButton = computed(
    () => this.resolvedConfig()['display_back_button'] as unknown as boolean,
  );
  readonly backButtonIconColor = computed(
    () => this.resolvedConfig()['back_button_icon_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly logoImageUrl = computed(() => readKioskHeaderLogoUrl(this.logo()));
  readonly displayLeavingSessionButton = computed(
    () => this.resolvedConfig()['display_leaving_session_button'] as unknown as boolean,
  );
  readonly displayAllergenAndTagFilters = computed(
    () => this.resolvedConfig()['display_allergen_and_tag_filters'] === true,
  );
  readonly buttonHomeText = computed(
    () => this.resolvedConfig()['button_home_text'] as unknown as string,
  );
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly size = computed(() => this.resolvedConfig()['size'] as unknown as 'L' | 'M' | 'S');
  readonly logoWidth = computed(
    () => this.resolvedConfig()['logo_width'] as unknown as 'small' | 'medium' | 'large',
  );
  readonly blocks = computed(() => this.resolvedConfig()['blocks'] as unknown as unknown[]);

  private readonly shopRefiner = inject(ShopStoreRefiner);
  private readonly catalogFilters = inject(ShopCatalogFiltersService);
  private readonly shopProducts = toSignal(this.shopRefiner.selectActiveShopProducts, {
    initialValue: [],
  });
  protected readonly categoryTree = toSignal(this.shopRefiner.selectActiveProductsCategories, {
    initialValue: [],
  });

  protected readonly catalogFiltersOpen = signal(false);
  protected readonly appliedCatalogFilters = this.catalogFilters.applied;
  protected readonly catalogFilterOptions = computed(() =>
    collectCatalogFilterOptions(this.shopProducts()),
  );
  protected readonly showAllergenFiltersButton = computed(
    () =>
      this.displayAllergenAndTagFilters() && hasCatalogFilterOptions(this.catalogFilterOptions()),
  );

  private readonly hideFiltersWhenDisabled = effect(() => {
    if (this.displayAllergenAndTagFilters()) {
      return;
    }
    this.catalogFiltersOpen.set(false);
    this.catalogFilters.clear();
  });

  protected openCatalogFilters(): void {
    this.catalogFiltersOpen.set(true);
  }

  protected applyCatalogFilters(filters: CatalogFilterSelection[]): void {
    this.catalogFilters.apply(filters);
    this.catalogFiltersOpen.set(false);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskHeaderBlockComponent, { 'keyAliases': {} });
